import * as React from 'react'
import styled from 'styled-components'
import * as moment from 'moment'
import { tJa } from '../../../../core/i18n'
import { IJsonResponse } from '../../../../core/JsonApiSerializer'
import { dmcTourService, utilService } from '../../../../core/services'
import * as constants from '../../../../static/constants'
import * as interfaces from '../../../../static/interfaces'
import * as Hooks from '../../../../utils/hooks'
import { Button } from '../../../atoms'
import SvgIcon from '../../../atoms/SvgIcon'
import SidebarLayout from '../../_layouts/sidebarLayout'
import { DmcSteps, Footer, Main, PageHead, Wrapper } from '../../../organisms/DmcTours/Frame'
import {
  GuestForm,
  IGuestFormValues,
  IRegisteredGuest,
  buildEmptyGuest,
} from '../../../organisms/DmcTours/GuestForm'

declare var window: interfaces.IWindow

interface IProps {
  user: any
  dmc_tour_id: number
  tour: {
    short_name: string
    area_name: string | null
    post_type: 'private' | 'group'
    exclusive: boolean
  }
  posts: Array<{ id: number; duration: number }>
  ota_reservations: IJsonResponse
}

// DMCツアーのゲスト情報入力画面。ゲスト(OTA予約)を追加して保存するとOPカレンダーへ進む
const DmcTourGuests: React.FC<IProps> = props => {
  const registeredGuests: IRegisteredGuest[] = React.useMemo(() => {
    const { data: otaReservations } = utilService.getDataFromJson(props.ota_reservations)
    return otaReservations.map(reservation => ({
      id: reservation.id,
      jwt_tour_id: reservation.jwt_tour_id,
      channel_manager: reservation.channel_manager,
      seller: reservation.seller,
      tour_date: reservation.tour_date,
      tour_start_at: reservation.tour_start_at
        ? moment
            .utc(reservation.tour_start_at)
            .utcOffset(9)
            .format('HH:mm')
        : '',
      customer_full_name: reservation.customer_full_name,
      customer_last_name: reservation.customer_last_name ?? '',
      customer_first_name: reservation.customer_first_name ?? '',
      customer_email: reservation.customer_email ?? '',
      customer_phone: reservation.customer_phone ?? '',
      other_requests: reservation.other_requests ?? '',
      num_of_adults: reservation.num_of_adults,
      num_of_children: reservation.num_of_children,
      num_of_infants: reservation.num_of_infants,
      duration: props.posts.find(p => p.id === reservation.jwt_post_id)?.duration ?? null,
    }))
  }, [props.ota_reservations, props.posts])

  Hooks.SetDocumentTitle(tJa('dmc_tour.title_guests'))

  // 登録済みゲストが無いときは空のゲストを1組出しておく
  const [guests, setGuests] = React.useState<IGuestFormValues[]>(() =>
    registeredGuests.length === 0 ? [buildEmptyGuest()] : []
  )
  const [isSubmitting, setIsSubmitting] = React.useState(false)
  const editPath = `/agent/dmc_tours/${props.dmc_tour_id}/edit`

  // Buttonはtext/backgroundColor指定がdisabledの見た目より優先されるため、無効時は色指定を外す
  const hasNoGuests = guests.length === 0 && registeredGuests.length === 0

  const handleSubmit = async () => {
    if (guests.length === 0) {
      // 登録済みゲストだけなら保存せず、最も早い催行日のツアーを開く
      const firstRegisteredGuest = registeredGuests
        .slice()
        .sort((a, b) => a.tour_date.localeCompare(b.tour_date))[0]
      if (firstRegisteredGuest) {
        const params = new URLSearchParams({ start: firstRegisteredGuest.tour_date })
        if (firstRegisteredGuest.jwt_tour_id) {
          params.set('tour_id', String(firstRegisteredGuest.jwt_tour_id))
        }
        window.location.href = `/agent/tours/weekly?${params.toString()}`
      }
      return
    }

    setIsSubmitting(true)
    try {
      const data = await dmcTourService.update(props.dmc_tour_id, {
        guests: guests.map(({ key, ...guest }) => guest),
      })
      window.flashMessages.addMessage({ text: data.flush.message, type: data.flush.type })
      window.location.href = data.redirect_path
    } catch (error) {
      // サーバエラーのメッセージ表示は httpClient のインターセプタが行うため、ここでは送信状態を戻すだけ
      setIsSubmitting(false)
    }
  }

  return (
    <SidebarLayout user={props.user}>
      <Wrapper>
        <PageHead>
          <h1>{tJa('dmc_tour.title_guests')}</h1>
        </PageHead>
        <Main>
          <DmcSteps current={1} />
          <TourSummary>
            <dl>
              <dt>{tJa('dmc_tour.created_tour')}</dt>
              <dd className="name">{props.tour.short_name}</dd>
            </dl>
            <dl>
              <dt>{tJa('generic.area')}</dt>
              <dd>{props.tour.area_name}</dd>
            </dl>
            <dl>
              <dt>{tJa('post.tour_type')}</dt>
              <dd>
                {tJa(
                  props.tour.post_type === 'private'
                    ? 'dmc_tour.private_tour'
                    : props.tour.exclusive
                    ? 'dmc_tour.exclusive_group'
                    : 'dmc_tour.custom_group'
                )}
              </dd>
            </dl>
            <a className="edit" href={editPath}>
              {tJa('dmc_tour.edit_tour')}
            </a>
          </TourSummary>
          <GuestForm guests={guests} onChange={setGuests} registeredGuests={registeredGuests} />
          <Footer>
            <Button>
              <a href={editPath}>
                <SvgIcon name="arrow_back" width={16} height={16} className="icon" />
                {tJa('dmc_tour.back_to_tour')}
              </a>
            </Button>
            <Button
              textColor={hasNoGuests ? undefined : constants.COLORS.white}
              backgroundColor={hasNoGuests ? undefined : constants.COLORS.primary}
              disabled={isSubmitting || hasNoGuests}
              handleClick={handleSubmit}
            >
              {tJa(
                isSubmitting
                  ? 'generic.saving'
                  : guests.length === 0
                  ? 'dmc_tour.go_to_calendar'
                  : 'dmc_tour.save_and_go_to_calendar'
              )}
              <SvgIcon name="arrow_forward" width={16} height={16} className="icon" />
            </Button>
          </Footer>
        </Main>
      </Wrapper>
    </SidebarLayout>
  )
}

const TourSummary = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 40px;
  margin-bottom: 16px;
  padding: 16px 24px;
  font-size: 13px;
  background-color: ${constants.COLORS.successBG2};
  border: 1px solid ${constants.COLORS.divider2};
  border-radius: 8px;

  dl {
    margin: 0;
  }

  dt {
    margin-bottom: 4px;
    font-size: 12px;
    color: ${constants.COLORS.subText};
  }

  dd {
    margin: 0;
    font-size: 14px;

    &.name {
      font-weight: bold;
      color: ${constants.COLORS.primary};
    }
  }

  .edit {
    margin-left: auto;
    font-size: 14px;
    color: ${constants.COLORS.primary};
    text-decoration: underline;
  }
`

export default DmcTourGuests
